/**
 * Global Error Handler Utility
 * Handles unhandled promise rejections and window errors to prevent white screen crashes
 */

import { logger } from '../services/logger';
import { toast } from 'react-hot-toast';

/**
 * Setup global error handlers for uncaught errors and promise rejections
 * Call this once at application startup
 */
export function setupGlobalErrorHandlers(): () => void {
  // Handler for unhandled promise rejections
  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    logger.error(
      '[GlobalErrorHandler] Unhandled Promise Rejection',
      {
        reason: event.reason,
        promise: event.promise,
        stack: event.reason?.stack,
      },
      'GlobalErrorHandler'
    );

    // Prevent default browser behavior (logging to console)
    event.preventDefault();

    // Show user-friendly error message
    console.error('💥 Unhandled Promise Rejection:', event.reason);

    // Show user-friendly error notification
    toast.error('Váratlan hiba történt. Ha a probléma továbbra is fennáll, frissítsd az oldalt.', {
      duration: 6000,
    });
  };

  // Handler for uncaught JavaScript errors
  const handleWindowError = (event: ErrorEvent) => {
    logger.error(
      '[GlobalErrorHandler] Uncaught JavaScript Error',
      {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        stack: event.error?.stack,
      },
      'GlobalErrorHandler'
    );

    // Log to console for debugging
    console.error('💥 Uncaught Error:', event.error || event.message);

    // Show user-friendly error notification (less frequent to avoid spam)
    if (!event.filename?.includes('extension')) {
      // Skip browser extension errors
      toast.error(
        'JavaScript hiba történt. Kérlek frissítsd az oldalt ha a probléma továbbra is fennáll.',
        {
          duration: 8000,
        }
      );
    }

    // Don't prevent default - let browser handle it normally
    return false;
  };

  // Register event handlers
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  window.addEventListener('error', handleWindowError);

  // Return cleanup function
  return () => {
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    window.removeEventListener('error', handleWindowError);
  };
}
